// src/components/PrivacyPolicyModal.js
import { useEffect, useRef } from "react";

export default function PrivacyPolicyModal({ onClose, lang = "en" }) {
  const closeBtnRef = useRef(null);

  // focus management
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  const title = lang === "fr" ? "Politique de confidentialité" : "Privacy Policy";

  return (
    <div style={overlayStyle} role="presentation" onMouseDown={onClose}>
      <div
        className="card"
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()} // prevent close when clicking inside
      >
        <div style={modalHeaderStyle}>
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--text)" }}>{title}</h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            style={closeStyle}
            aria-label={lang === "fr" ? "Fermer" : "Close"}
          >
            ✕
          </button>
        </div>

        <div style={contentStyle}>
          {lang === "fr" ? (
            <>
              <p><strong>Quels sont les renseignements que nous recueillons?</strong></p>
              <p>
                Nous recueillons des renseignements personnels lorsque vous soumettez votre commande par
                l’entremise de notre portail Costco.ca. Lorsque vous passez une commande ou que vous vous
                inscrivez sur notre site, selon le cas, vous pourrez être invité à entrer les renseignements
                suivants : nom, adresse de courriel, adresse postale, numéro de téléphone. Vous pouvez toutefois
                visiter notre site de façon anonyme.
              </p>
              <p>
                Google, en tant que prestataire tiers, utilise des témoins pour diffuser des publicités aux visiteurs.
                Google utilise le témoin DART pour adapter les annonces aux visiteurs en fonction de leur navigation sur
                notre site Web et sur d’autres sites. Les visiteurs peuvent renoncer à l’usage du témoin DART en consultant
                la politique de confidentialité de Google en matière de publicité et de réseau de contenu.
              </p>

              <p><strong>Comment utilisons-nous les renseignements que nous recueillons?</strong></p>
              <p>
                Tous les renseignements personnels que nous recueillons peuvent être utilisés à l’une des fins suivantes:
                améliorer le service à la clientèle (vos renseignements nous aident à mieux vous servir et à répondre à vos besoins)
                traiter des transactions (vos renseignements, qu’ils soient publics ou privés, ne seront pas vendus, échangés,
                transférés ni donnés à une autre société pour quelque raison que ce soit sans votre consentement, sauf dans le but
                déclaré de livrer le produit acheté ou le service demandé)
              </p>
              <p>
                L’adresse de courriel que vous fournissez pour le traitement de votre commande ne servira qu’à la transmission des
                renseignements et des mises à jour relatives à votre commande.
              </p>

              <p><strong>Quelles mesures prenons-nous pour nous assurer que l’accès à vos renseignements est protégé?</strong></p>
              <p>
                Nous mettons en œuvre diverses mesures de sécurité afin de protéger l’accès à vos renseignements personnels lorsque
                vous passez une commande ou que vous entrez, soumettez ou consultez vos renseignements personnels. Nous utilisons un
                serveur sécurisé. Tous les renseignements de nature délicate ou concernant les cartes de crédit sont transmis au moyen
                du protocole SSL (couche de sockets sécurisés) puis cryptés dans notre base de données des services d’hébergement et ne
                seront accessibles que par les personnes autorisées disposant des droits d’accès requis pour ces systèmes et qui sont tenues
                de respecter la confidentialité de ces renseignements. Après une transaction, vos renseignements personnels ne seront pas
                conservés sur nos serveurs.
              </p>
            </>
          ) : (
            <>
              <p><strong>What information do we collect?</strong></p>
              <p>
                We collect information from you when you submit your order VIA our portal designed for Costco.ca
                When ordering or registering on our site, as appropriate, you may be asked to enter your: name,
                e-mail address, mailing address or phone number. You may, however, visit our site anonymously.
              </p>

              <p>
                Google, as a third party vendor, uses cookies to serve ads on your site. Google's use of the DART cookie
                enables it to serve ads to your users based on their visit to your sites and other sites on the Internet.
                Users may opt out of the use of the DART cookie by visiting the Google ad and content network privacy policy.
              </p>

              <p><strong>What do we use your information for?</strong></p>
              <p>Any of the information we collect from you may be used in one of the following ways:</p>
              <ul>
                <li>
                  To improve customer service (your information helps us to more effectively respond to your customer service
                  requests and support needs)
                </li>
                <li>
                  To process transactions (Your information, whether public or private, will not be sold, exchanged,
                  transferred, or given to any other company for any reason whatsoever, without your consent, other than
                  for the express purpose of delivering the purchased product or service requested.)
                </li>
              </ul>

              <p>
                The email address you provide for order processing, will only be used to send you information and updates
                pertaining to your order.
              </p>

              <p><strong>How do we protect your information?</strong></p>
              <p>
                We implement a variety of security measures to maintain the safety of your personal information when you place
                an order or enter, submit, or access your personal information. We offer the use of a secure server. All supplied
                sensitive/credit information is transmitted via Secure Socket Layer (SSL) technology and then encrypted into our
                hostingproviders database only to be accessible by those authorized with special access rights to such systems, and
                are required to keep the information confidential. After a transaction, your private information will not be stored
                on our servers.
              </p>
            </>
          )}
        </div>

        <div style={modalFooterStyle}>
          <button type="button" onClick={onClose} style={primaryBtnStyle}>
            {lang === "fr" ? "Fermer" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const modalStyle = {
  width: "min(900px, 96vw)",
  maxHeight: "86vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  // Let your .card class + CSS variables control the main look
  background: "var(--card)",
  color: "var(--text)",
  boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
};

const modalHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 16px",
  borderBottom: "1px solid var(--border)",
};

const contentStyle = {
  padding: "14px 16px",
  overflowY: "auto",
  fontSize: 14,
  lineHeight: 1.55,
  color: "var(--text)",
  maxWidth: 820,
  margin: "0 auto",
};

const modalFooterStyle = {
  padding: "12px 16px",
  borderTop: "1px solid var(--border)",
  display: "flex",
  justifyContent: "flex-end",
};

const closeStyle = {
  border: "none",
  background: "transparent",
  color: "var(--text)",
  fontSize: 18,
  cursor: "pointer",
  lineHeight: 1,
};

const primaryBtnStyle = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--card)",
  color: "var(--text)",
  cursor: "pointer",
};
